import { parseNumber } from "./index";

// Old implementation kept for testing edge cases
const numberPrefixRegexp = /^-?[0-9,.]+/;

export function getNumberPrefixOld(s: string): { n: number; rest: string } | undefined {
    const match = s.match(numberPrefixRegexp);
    if (match === null) return undefined;

    const numberString = match[0];
    const n = parseNumber(numberString);

    if (n === undefined) return undefined;

    const exceedsSafeInt = n > Number.MAX_SAFE_INTEGER || n < Number.MIN_SAFE_INTEGER;
    if (exceedsSafeInt) {
        return undefined;
    }
    return {
        n,
        rest: s.substring(numberString.length),
    };
}

// Character codes for fast comparison
const CHAR_0 = 48; // '0'
const CHAR_9 = 57; // '9'
const CHAR_DOT = 46; // '.'
const CHAR_COMMA = 44; // ','
const CHAR_MINUS = 45; // '-'

const enum State {
    Start,
    AfterMinus,
    Integer,
    AfterDot,
    Fraction,
    AfterComma,
    AfterCommaDigits,
}

// Exported only for testing
export function getNumberPrefix(s: string): { n: number; rest: string } | undefined {
    const len = s.length;
    if (len === 0) return undefined;

    let i = 0;
    let state: State = State.Start;
    let sign = 1;
    let integerPart = 0;
    let fractionPart = 0;
    let fractionDivisor = 1;
    let hasDot = false;
    let hasDigit = false; // Must have at least one digit to be valid
    let commaCount = 0; // Track number of commas seen (without intervening dot)
    let commaValue = 0; // Value after comma (treated as decimal if no dot)
    let commaDivisor = 1;

    while (i < len) {
        const c = s.charCodeAt(i);

        switch (state) {
            case State.Start:
                if (c === CHAR_MINUS) {
                    sign = -1;
                    state = State.AfterMinus;
                } else if (c >= CHAR_0 && c <= CHAR_9) {
                    integerPart = c - CHAR_0;
                    hasDigit = true;
                    state = State.Integer;
                } else if (c === CHAR_DOT) {
                    hasDot = true;
                    state = State.AfterDot;
                } else if (c === CHAR_COMMA) {
                    commaCount = 1;
                    state = State.AfterComma;
                } else {
                    return undefined;
                }
                break;

            case State.AfterMinus:
                if (c >= CHAR_0 && c <= CHAR_9) {
                    integerPart = c - CHAR_0;
                    hasDigit = true;
                    state = State.Integer;
                } else if (c === CHAR_DOT) {
                    hasDot = true;
                    state = State.AfterDot;
                } else if (c === CHAR_COMMA) {
                    commaCount = 1;
                    state = State.AfterComma;
                } else {
                    return undefined;
                }
                break;

            case State.Integer:
                if (c >= CHAR_0 && c <= CHAR_9) {
                    integerPart = integerPart * 10 + (c - CHAR_0);
                    hasDigit = true;
                } else if (c === CHAR_DOT) {
                    hasDot = true;
                    state = State.AfterDot;
                } else if (c === CHAR_COMMA) {
                    commaCount = 1;
                    state = State.AfterComma;
                } else {
                    break;
                }
                break;

            case State.AfterDot:
                if (c >= CHAR_0 && c <= CHAR_9) {
                    fractionPart = fractionPart * 10 + (c - CHAR_0);
                    fractionDivisor *= 10;
                    hasDigit = true;
                    state = State.Fraction;
                } else if (c === CHAR_DOT) {
                    // Multiple dots like "1.2.3" - fails
                    return undefined;
                } else if (c === CHAR_COMMA) {
                    // Dot then comma - comma is ignored (thousand separator logic)
                    // But this is weird: "1.,5" - stay in AfterDot looking for fraction digits
                } else {
                    // End of number (e.g., "1." -> 1)
                    break;
                }
                break;

            case State.Fraction:
                if (c >= CHAR_0 && c <= CHAR_9) {
                    fractionPart = fractionPart * 10 + (c - CHAR_0);
                    fractionDivisor *= 10;
                    hasDigit = true;
                } else if (c === CHAR_DOT) {
                    // Second dot in fraction like "1.2.3" - fails
                    return undefined;
                } else if (c === CHAR_COMMA) {
                    // Comma after fraction digits - ignored (thousand separator)
                    // Stay in Fraction state
                } else {
                    break;
                }
                break;

            case State.AfterComma:
                if (c >= CHAR_0 && c <= CHAR_9) {
                    commaValue = c - CHAR_0;
                    commaDivisor = 10;
                    hasDigit = true;
                    state = State.AfterCommaDigits;
                } else if (c === CHAR_DOT) {
                    // Comma then dot - dot takes precedence
                    hasDot = true;
                    commaCount = 0; // Reset - commas before dot are thousand separators
                    state = State.AfterDot;
                } else if (c === CHAR_COMMA) {
                    // Consecutive commas ",," - need to track for later
                    // If we later see a dot, commas are removed so ",," is ok
                    // If no dot, commas become dots so ",," becomes ".." which fails
                    commaCount++;
                } else {
                    // End of number (e.g., "1," -> 1)
                    break;
                }
                break;

            case State.AfterCommaDigits:
                if (c >= CHAR_0 && c <= CHAR_9) {
                    commaValue = commaValue * 10 + (c - CHAR_0);
                    commaDivisor *= 10;
                    hasDigit = true;
                } else if (c === CHAR_DOT) {
                    // Comma then digits then dot - comma was thousand separator
                    // e.g., "1,000.50"
                    hasDot = true;
                    integerPart = integerPart * commaDivisor + commaValue;
                    commaValue = 0;
                    commaDivisor = 1;
                    commaCount = 0; // Reset - we now know commas were thousand separators
                    state = State.AfterDot;
                } else if (c === CHAR_COMMA) {
                    // Multiple commas without dot yet
                    // Accumulate into integer part and continue
                    integerPart = integerPart * commaDivisor + commaValue;
                    commaValue = 0;
                    commaDivisor = 1;
                    commaCount++;
                    state = State.AfterComma;
                } else {
                    break;
                }
                break;
        }

        // Check if we broke out of switch without advancing
        if (
            state === State.Integer ||
            state === State.Fraction ||
            state === State.AfterDot ||
            state === State.AfterComma ||
            state === State.AfterCommaDigits
        ) {
            const c2 = s.charCodeAt(i);
            if (
                !(c2 >= CHAR_0 && c2 <= CHAR_9) &&
                c2 !== CHAR_DOT &&
                c2 !== CHAR_COMMA &&
                !(i === 0 && c2 === CHAR_MINUS)
            ) {
                break;
            }
        }

        i++;
    }

    // Must have consumed at least one character and have at least one digit
    if (i === 0 || !hasDigit) return undefined;

    // If we saw multiple commas without a dot, that's invalid
    // e.g., "1,000,000" would have commaCount >= 2
    if (commaCount >= 2) {
        return undefined;
    }

    let n: number;

    if (hasDot) {
        // Dot was present - commas were thousand separators (already accumulated into integerPart)
        n = sign * (integerPart + fractionPart / fractionDivisor);
    } else if (commaCount === 1) {
        // Single comma, no dot - comma acts as decimal point
        // integerPart is the part before comma, commaValue/commaDivisor is after
        n = sign * (integerPart + commaValue / commaDivisor);
    } else {
        n = sign * integerPart;
    }

    if (n > Number.MAX_SAFE_INTEGER || n < Number.MIN_SAFE_INTEGER) {
        return undefined;
    }

    return {
        n,
        rest: s.substring(i),
    };
}

function asMaybeTrueOrFalse(s: string): boolean | undefined {
    s = s.toLowerCase();
    if (s === "true" || s === "1") return true;
    if (s === "false" || s === "0") return false;
    return undefined;
}

// ##compareStrings:
// We handle numbers and booleans specially.
export function compareStrings(one: string, two: string): number {
    const p1 = getNumberPrefix(one);
    if (p1 !== undefined) {
        const p2 = getNumberPrefix(two);
        if (p2 !== undefined) {
            if (p1.n < p2.n) return -1;
            if (p1.n > p2.n) return 1;

            one = p1.rest;
            two = p2.rest;
        }
    }

    // FIXME: This leads to inconsistent results.  `false` is less than `1`,
    // but `fa` is greater than `1`, but `fa` is also less than `false`.  So
    //
    //   false < 1 < fa < false
    const b1 = asMaybeTrueOrFalse(one);
    if (b1 !== undefined) {
        const b2 = asMaybeTrueOrFalse(two);
        if (b2 !== undefined) {
            if (b1 === b2) return 0;
            if (b1 === false) return -1;
            return 1;
        }
    }

    return one.localeCompare(two);
}
